import React, { useState, useEffect } from 'react';
import { 
    Button, 
    Variants, 
    Table, 
    THead, 
    TBody, 
    TR, 
    TH, 
    TD,
    Toggle,
    TextInput,
    Icons,
    Heading,
    Tooltip,
    Positions
} from '@sede-enterprise/shell-ds-react-framework';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col } from '../../components/flexbox/Flex';
import { AnchorSpan, ContainerDiv } from '../../components/styledComponent/StyledComponent';
import { usePageTitle } from '../../services/UtilService';
import { useTranslation } from 'react-i18next';
import { getData } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import { useToast } from 'rc-toastr';
import Loader from '../../components/loader/Loader';
import './styles.css';

interface ReportListItem {
    id: number;
    reportName: string;
    reportDescription: string;
    filterCriteria: string;
    filterCriteriaValue: string;
    selectedGroups: string;
    selectedColumns: string;
    isActive: boolean;
    isStatic?: boolean | number | string | null;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
}

const ConfigureReportList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { toast } = useToast();
    usePageTitle(t('shellAviationAIMS') + " | Reports");

    const [reports, setReports] = useState<ReportListItem[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Fetch reports from API
    const fetchReports = (search = '') => {
        setIsLoading(true);
        const url = `${ApiUrls.reportList}?searchTerm=${encodeURIComponent(search)}`;
        
        getData(url)
            .then((response) => {
                console.log('Report list response:', response);
                const apiReports = response.data?.data ?? [];
                const filteredReports = (Array.isArray(apiReports) ? apiReports : []).filter((item: ReportListItem) => {
                    const flag = item?.isStatic;
                    return !(flag === true || flag === 1 || flag === '1');
                });
                setReports(filteredReports);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Failed to load reports:', error);
                toast.error('Failed to load reports');
                setIsLoading(false);
            });
    };

    // Load reports on component mount
    useEffect(() => {
        fetchReports();
    }, []);

    // Sort reports: active first, then by newest
    const sortedReports = [...reports].sort((a, b) => {
        // First sort by isActive (active first)
        if (a.isActive !== b.isActive) {
            return a.isActive ? -1 : 1;
        }
        // Then sort by createdOn (newest first)
        return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
    });

    const handleAddNew = () => {
        navigate('/report/configure/new');
    };

    const handleEdit = (reportId: string) => {
        navigate(`/report/configure/${reportId}`);
    };

    const handleClone = (reportId: string) => {
        navigate(`/report/configure/new?clone=${reportId}`);
    };

    const handleSearch = () => {
        fetchReports(searchTerm);
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <ContainerDiv className="report-list-container">
            {/* Page Title at Top */}
            <div style={{ marginBottom: '20px' }}>
                <Row>
                    <Col size="100">
                        <Heading level={2}>Reports</Heading>
                    </Col>
                </Row>
            </div>

            {/* Second Row: Add New button and Search */}
            <div style={{ marginBottom: '20px' }}>
                <Row>
                    <Col size="50">
                        <Button 
                            variant={Variants.Filled}
                            onClick={handleAddNew}
                            icon={<Icons.Add />}
                        >
                            Add New
                        </Button>
                    </Col>
                    <Col size="50">
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '5px', width: '50%' }}>
                                <TextInput
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    placeholder="Search reports..."
                                    style={{ width: '100%' }}
                                />
                                <Icons.Search />
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Reports Table */}
            <Row>
                <Col size="100">
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                            <Loader />
                        </div>
                    ) : (
                        <Table>
                            <THead>
                                <TR>
                                    <TH>Report Name</TH>
                                    <TH>Description</TH>
                                    <TH>Filter</TH>
                                    <TH>Group</TH>
                                    <TH>Created By</TH>
                                    <TH>Is Active</TH>
                                    <TH>Actions</TH>
                                </TR>
                            </THead>
                            <TBody>
                                {sortedReports.length > 0 ? (
                                    sortedReports.map((report) => (
                                        <TR key={report.id}>
                                            <TD style={{ maxWidth: '320px' }}>
                                                {(() => {
                                                    const fullName = report.reportName || '-';
                                                    const truncatedName = fullName.length > 100
                                                        ? `${fullName.slice(0, 100)}...`
                                                        : fullName;

                                                    return (
                                                        <Tooltip
                                                            placement={Positions.Top}
                                                            overlay={<span style={{ display: 'block', maxWidth: '360px' }}>{fullName}</span>}
                                                            trigger={
                                                                <Link to={`/report/view/${report.id}`}>
                                                                    <AnchorSpan
                                                                        style={{
                                                                            display: 'inline-block',
                                                                            maxWidth: '100%',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                    >
                                                                        {truncatedName}
                                                                    </AnchorSpan>
                                                                </Link>
                                                            }
                                                        />
                                                    );
                                                })()}
                                            </TD>
                                            <TD style={{ maxWidth: '360px' }}>
                                                {(() => {
                                                    const fullDescription = report.reportDescription || '-';
                                                    const truncatedDescription = fullDescription.length > 100
                                                        ? `${fullDescription.slice(0, 100)}...`
                                                        : fullDescription;

                                                    return (
                                                        <Tooltip
                                                            placement={Positions.Top}
                                                            overlay={<span style={{ display: 'block', maxWidth: '400px' }}>{fullDescription}</span>}
                                                            trigger={
                                                                <span
                                                                    style={{
                                                                        display: 'inline-block',
                                                                        maxWidth: '100%',
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                >
                                                                    {truncatedDescription}
                                                                </span>
                                                            }
                                                        />
                                                    );
                                                })()}
                                            </TD>
                                            <TD>{report.filterCriteria || '-'}</TD>
                                            <TD>{report.selectedGroups || '-'}</TD>
                                            <TD>{report.createdBy || '-'}</TD>
                                            <TD>
                                                <span style={{
                                                    color: report.isActive ? '#28a745' : '#6c757d',
                                                    fontWeight: report.isActive ? 'bold' : 'normal',
                                                    fontSize: '14px'
                                                }}>
                                                    {report.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </TD>
                                            <TD>
                                                <div style={{display: 'flex', gap: '8px'}}>
                                                    <Button
                                                        variant={Variants.Outlined}
                                                        onClick={() => handleEdit(report.id.toString())}
                                                        size="small"
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant={Variants.Outlined}
                                                        onClick={() => handleClone(report.id.toString())}
                                                        size="small"
                                                    >
                                                        Clone
                                                    </Button>
                                                </div>
                                            </TD>
                                        </TR>
                                    ))
                                ) : (
                                    <TR>
                                        <TD colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                                            No reports found
                                        </TD>
                                    </TR>
                                )}
                            </TBody>
                        </Table>
                    )}
                </Col>
            </Row>
        </ContainerDiv>
    );
};

export default ConfigureReportList;
